import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { CalendarDay } from '@/lib/calendar';
import { TimeRecord, } from '@/types';
import { useAuth } from '@/hooks/useAuth';
import { useProjects } from '@/hooks/useProjects';
import { updateTimeRecordDetails, deleteTimestamp, addTimestamp } from '@/lib/timeTracking';
import { Timestamp } from 'firebase/firestore';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Check, 
  X, 
  Clock,
  Calendar as CalendarIcon,
  Save,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

interface DayViewProps {
  day: CalendarDay;
  onNavigate: (direction: 'prev' | 'next') => void;
  onViewChange?: (view: 'month' | 'week' | 'day') => void;
}

interface EditingRecord {
  id: string;
  clockInTime: string;
  clockOutTime: string;
  projectId: string;
  memo: string;
  categories: string[];
}

export function DayView({ day, onNavigate, onViewChange }: DayViewProps) {
  const { user } = useAuth();
  const { projects } = useProjects('active');
  
  const [records, setRecords] = useState<TimeRecord[]>(day.records || []);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<EditingRecord | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [newRecord, setNewRecord] = useState({
    clockInTime: '09:00',
    clockOutTime: '18:00',
    projectId: '',
    memo: '',
    categories: [] as string[],
  });
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    setRecords(day.records || []);
  }, [day]);

  const handleEdit = (record: TimeRecord) => {
    const clockInDate = record.clockInTime.toDate();
    const clockOutDate = record.clockOutTime?.toDate();
    
    setEditingId(record.id);
    setEditingData({
      id: record.id,
      clockInTime: format(clockInDate, 'HH:mm'),
      clockOutTime: clockOutDate ? format(clockOutDate, 'HH:mm') : '',
      projectId: record.projectId || '',
      memo: record.memo || '',
      categories: record.categories || [],
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData(null);
  };

  const handleSaveEdit = async () => {
    if (!user || !editingData) return;

    setIsProcessing(true);
    try {
      const [inHour, inMin] = editingData.clockInTime.split(':').map(Number);
      const [outHour, outMin] = editingData.clockOutTime.split(':').map(Number);
      
      const clockInDate = new Date(day.date);
      clockInDate.setHours(inHour, inMin, 0, 0);
      
      const clockOutDate = new Date(day.date);
      clockOutDate.setHours(outHour, outMin, 0, 0);

      const project = projects.find((p) => p.id === editingData.projectId);
      
      const data: any = {
        clockInTime: Timestamp.fromDate(clockInDate),
        clockOutTime: Timestamp.fromDate(clockOutDate),
        projectId: editingData.projectId,
        project: project ? {
          id: project.id,
          name: project.name,
          code: project.code || '',
          client: project.client || null,
        } : null,
        memo: editingData.memo,
        categories: editingData.categories,
      };

      await updateTimeRecordDetails(
        user.uid,
        editingData.id,
        data,
        user.email || 'unknown'
      );

      // レコードを更新
      setRecords((prev) =>
        prev.map((r) =>
          r.id === editingData.id
            ? {
                ...r,
                clockInTime: data.clockInTime,
                clockOutTime: data.clockOutTime,
                projectId: data.projectId,
                project: data.project,
                memo: data.memo,
                categories: data.categories,
                totalMinutes: Math.floor(
                  (clockOutDate.getTime() - clockInDate.getTime()) / 60000
                ),
              }
            : r
        )
      );

      setEditingId(null);
      setEditingData(null);
      alert('更新しました');
    } catch (error) {
      console.error('Save error:', error);
      alert('更新に失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async (recordId: string) => {
    if (!user) return;
    if (!window.confirm('この記録を削除しますか？')) return;

    setIsProcessing(true);
    try {
      await deleteTimestamp(user.uid, recordId);
      setRecords((prev) => prev.filter((r) => r.id !== recordId));
      alert('削除しました');
    } catch (error) {
      console.error('Delete error:', error);
      alert('削除に失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddRecord = async () => {
    if (!user) return;
    if (!newRecord.projectId) {
      alert('プロジェクトを選択してください');
      return;
    }

    setIsProcessing(true);
    try {
      const [inHour, inMin] = newRecord.clockInTime.split(':').map(Number);
      const [outHour, outMin] = newRecord.clockOutTime.split(':').map(Number);
      
      const clockInDate = new Date(day.date);
      clockInDate.setHours(inHour, inMin, 0, 0);
      
      const clockOutDate = new Date(day.date);
      clockOutDate.setHours(outHour, outMin, 0, 0);

      const project = projects.find((p) => p.id === newRecord.projectId);
      if (!project) {
        alert('プロジェクトが見つかりません');
        return;
      }

      const totalMinutes = Math.floor(
        (clockOutDate.getTime() - clockInDate.getTime()) / 60000
      );

      await addTimestamp(user.uid, {
        clockInTime: Timestamp.fromDate(clockInDate),
        clockOutTime: Timestamp.fromDate(clockOutDate),
        projectId: project.id,
        project: {
          id: project.id,
          name: project.name,
          code: project.code || '',
          client: project.client || null,
        },
        categories: newRecord.categories,
        memo: newRecord.memo,
        totalMinutes,
        status: 'completed',
        recordType: 'manual',
      });

      // レコードを追加（仮のIDで）
      const newRecordData: TimeRecord = {
        id: Date.now().toString(),
        userId: user.uid,
        projectId: project.id,
        project: {
          id: project.id,
          name: project.name,
          code: project.code || '',
          client: project.client || null,
        },
        clockInTime: Timestamp.fromDate(clockInDate),
        clockOutTime: Timestamp.fromDate(clockOutDate),
        totalMinutes,
        status: 'completed',
        memo: newRecord.memo,
        categories: newRecord.categories,
        recordType: 'manual',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      setRecords((prev) => [...prev, newRecordData].sort((a, b) => 
        a.clockInTime.toMillis() - b.clockInTime.toMillis()
      ));

      // フォームをリセット
      setNewRecord({
        clockInTime: '09:00',
        clockOutTime: '18:00',
        projectId: '',
        memo: '',
        categories: [],
      });
      setIsAdding(false);
      alert('追加しました');
    } catch (error) {
      console.error('Add error:', error);
      alert('追加に失敗しました');
    } finally {
      setIsProcessing(false);
    }
  };

  const totalMinutes = records.reduce((sum, r) => sum + (r.totalMinutes || 0), 0);
  const totalHours = Math.floor(totalMinutes / 60);
  const totalMins = totalMinutes % 60;

  const categoryOptions = ['会議', '作業', 'その他'];

  return (
    <div className="space-y-6">
      {/* 日付ヘッダー */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => onNavigate('prev')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="前日"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>

          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <CalendarIcon className="w-5 h-5 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">
                {format(day.date, 'yyyy年M月d日（E）', { locale: ja })}
              </h2>
            </div>
            {day.isToday && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                今日
              </span>
            )}
          </div>

          <button
            onClick={() => onNavigate('next')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="翌日"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* ビュー切り替えボタン */}
        {onViewChange && (
          <div className="flex justify-center gap-2 pt-4 border-t">
            <button
              onClick={() => onViewChange('month')}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              月表示に戻る
            </button>
            <button
              onClick={() => onViewChange('week')}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              週表示に戻る
            </button>
          </div>
        )}

        {/* サマリー */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t">
          <div className="text-center">
            <div className="text-sm text-gray-600 mb-1">稼働記録</div>
            <div className="text-2xl font-bold text-gray-900">{records.length}件</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-gray-600 mb-1">合計時間</div>
            <div className="text-2xl font-bold text-blue-600">
              {totalHours}h {totalMins}m
            </div>
          </div>
          <div className="text-center">
            <button
              onClick={() => setIsAdding(true)}
              disabled={isAdding || isProcessing}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              記録を追加
            </button>
          </div>
        </div>
      </div>

      {/* 新規追加フォーム */}
      {isAdding && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-md p-6 border-2 border-blue-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-600" />
              新しい稼働記録
            </h3>
            <button
              onClick={() => setIsAdding(false)}
              className="p-1 hover:bg-white rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                開始時刻
              </label>
              <input
                type="time"
                value={newRecord.clockInTime}
                onChange={(e) => setNewRecord({ ...newRecord, clockInTime: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                終了時刻
              </label>
              <input
                type="time"
                value={newRecord.clockOutTime}
                onChange={(e) => setNewRecord({ ...newRecord, clockOutTime: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                プロジェクト
              </label>
              <select
                value={newRecord.projectId}
                onChange={(e) => setNewRecord({ ...newRecord, projectId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">選択してください</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name} {project.code ? `(${project.code})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                カテゴリ
              </label>
              <div className="flex gap-2">
                {categoryOptions.map((cat) => (
                  <label key={cat} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newRecord.categories.includes(cat)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewRecord({
                            ...newRecord,
                            categories: [...newRecord.categories, cat],
                          });
                        } else {
                          setNewRecord({
                            ...newRecord,
                            categories: newRecord.categories.filter((c) => c !== cat),
                          });
                        }
                      }}
                      className="mr-1"
                    />
                    {cat}
                  </label>
                ))}
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                備考
              </label>
              <textarea
                value={newRecord.memo}
                onChange={(e) => setNewRecord({ ...newRecord, memo: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="作業内容など..."
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={() => setIsAdding(false)}
              disabled={isProcessing}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:bg-gray-100"
            >
              キャンセル
            </button>
            <button
              onClick={handleAddRecord}
              disabled={isProcessing || !newRecord.projectId}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              追加
            </button>
          </div>
        </div>
      )}

      {/* タイムライン */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-blue-600" />
          タイムライン
        </h3>

        {records.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">この日の稼働記録はありません</p>
            <button
              onClick={() => setIsAdding(true)}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              記録を追加
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {records.map((record) => {
              const isEditing = editingId === record.id;
              const clockInDate = record.clockInTime.toDate();
              const clockOutDate = record.clockOutTime?.toDate();
              const duration = record.totalMinutes || 0;
              const hours = Math.floor(duration / 60);
              const mins = duration % 60;

              if (isEditing && editingData) {
                // 編集モード
                return (
                  <div
                    key={record.id}
                    className="border-2 border-blue-400 rounded-lg p-4 bg-blue-50"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          開始時刻
                        </label>
                        <input
                          type="time"
                          value={editingData.clockInTime}
                          onChange={(e) =>
                            setEditingData({ ...editingData, clockInTime: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          終了時刻
                        </label>
                        <input
                          type="time"
                          value={editingData.clockOutTime}
                          onChange={(e) =>
                            setEditingData({ ...editingData, clockOutTime: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          プロジェクト
                        </label>
                        <select
                          value={editingData.projectId}
                          onChange={(e) =>
                            setEditingData({ ...editingData, projectId: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">選択してください</option>
                          {projects.map((project) => (
                            <option key={project.id} value={project.id}>
                              {project.name} {project.code ? `(${project.code})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          カテゴリ
                        </label>
                        <div className="flex gap-2">
                          {categoryOptions.map((cat) => (
                            <label key={cat} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={editingData.categories.includes(cat)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setEditingData({
                                      ...editingData,
                                      categories: [...editingData.categories, cat],
                                    });
                                  } else {
                                    setEditingData({
                                      ...editingData,
                                      categories: editingData.categories.filter((c) => c !== cat),
                                    });
                                  }
                                }}
                                className="mr-1"
                              />
                              {cat}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          備考
                        </label>
                        <textarea
                          value={editingData.memo}
                          onChange={(e) =>
                            setEditingData({ ...editingData, memo: e.target.value })
                          }
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-4">
                      <button
                        onClick={handleCancelEdit}
                        disabled={isProcessing}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:bg-gray-100 flex items-center gap-2"
                      >
                        <X className="w-4 h-4" />
                        キャンセル
                      </button>
                      <button
                        onClick={handleSaveEdit}
                        disabled={isProcessing}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 flex items-center gap-2"
                      >
                        <Check className="w-4 h-4" />
                        保存
                      </button>
                    </div>
                  </div>
                );
              }

              // 表示モード
              return (
                <div
                  key={record.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-grow">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-semibold text-gray-900">
                            {format(clockInDate, 'HH:mm')}
                          </span>
                          <span className="text-gray-400">→</span>
                          <span className="text-lg font-semibold text-gray-900">
                            {clockOutDate ? format(clockOutDate, 'HH:mm') : '---'}
                          </span>
                        </div>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                          {hours}h {mins}m
                        </span>
                      </div>

                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-gray-900">
                          {record.project?.name || '未割り当て'}
                        </span>
                        {record.project?.code && (
                          <span className="text-xs text-gray-500">({record.project.code})</span>
                        )}
                        {record.recordType && (
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                              record.recordType === 'clock'
                                ? 'bg-green-100 text-green-800'
                                : record.recordType === 'manual'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-purple-100 text-purple-800'
                            }`}
                          >
                            {record.recordType === 'clock'
                              ? '打刻'
                              : record.recordType === 'manual'
                              ? '手動'
                              : 'カレンダー'}
                          </span>
                        )}
                      </div>

                      {record.categories && record.categories.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {record.categories.map((cat, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700"
                            >
                              {cat}
                            </span>
                          ))}
                        </div>
                      )}

                      {record.memo && (
                        <p className="text-sm text-gray-600 mt-2">{record.memo}</p>
                      )}
                    </div>

                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleEdit(record)}
                        disabled={isProcessing}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="編集"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(record.id)}
                        disabled={isProcessing}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="削除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
